// Sprint 1 — Onboarding post-registro
import { createContext, useContext, useMemo, useReducer, type ReactNode } from "react";

export type DayRange = { start: string; end: string };

export type DayConfig = {
  day: number;
  enabled: boolean;
  ranges: DayRange[];
};

export type WizardState = {
  step: number;
  accountType: "PROFESSIONAL" | "FACILITY";
  rubro: string;
  /** Sub-opciones del rubro elegido (ej. deportes ofrecidos). Ver src/lib/rubro-catalog.ts. */
  subRubro: string[];
  name: string;
  modality: "" | "virtual" | "presencial";
  phone: string;
  slotDuration: number;
  interval: number;
  slotsPerHour: number;
  anticipationHours: number;
  days: DayConfig[];
};

export type WizardAction =
  | { type: "SET_FIELD"; field: keyof Omit<WizardState, "days" | "subRubro">; value: string | number }
  | { type: "SET_SUBRUBRO"; value: string[] }
  | { type: "SET_DAY_CONFIG"; day: number; config: Partial<DayConfig> }
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "RESET" };

function initialDays(): DayConfig[] {
  return Array.from({ length: 7 }, (_, day) => ({
    day,
    enabled: false,
    ranges: [{ start: "09:00", end: "17:00" }],
  }));
}

export const initialWizardState: WizardState = {
  step: 0,
  accountType: "PROFESSIONAL",
  rubro: "",
  subRubro: [],
  name: "",
  modality: "",
  phone: "",
  slotDuration: 30,
  interval: 0,
  slotsPerHour: 1,
  anticipationHours: 12,
  days: initialDays(),
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_FIELD": {
      if (action.field === "days") return state;
      return { ...state, [action.field]: action.value } as WizardState;
    }
    case "SET_SUBRUBRO":
      return { ...state, subRubro: action.value };
    case "SET_DAY_CONFIG":
      return {
        ...state,
        days: state.days.map((d) => (d.day === action.day ? { ...d, ...action.config } : d)),
      };
    case "NEXT_STEP":
      return { ...state, step: Math.min(state.step + 1, 8) };
    case "PREV_STEP":
      return { ...state, step: Math.max(state.step - 1, 0) };
    case "RESET":
      return { ...initialWizardState, days: initialDays() };
    default:
      return state;
  }
}

type Ctx = {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
};

const OnboardingWizardContext = createContext<Ctx | null>(null);

export function OnboardingWizardProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);
  return <OnboardingWizardContext.Provider value={value}>{children}</OnboardingWizardContext.Provider>;
}

export function useOnboardingWizard() {
  const c = useContext(OnboardingWizardContext);
  if (!c) throw new Error("useOnboardingWizard debe usarse dentro de OnboardingWizardProvider");
  return c;
}
